import { EditorState } from 'prosemirror-state';
import { uiStatePluginKey, annotationPluginKey, trackedPluginKey, coordsAtPos } from '../editor-core';

/**
 * Internal API event
 * @property {Error} error - The error raised from the editor
 */
export const ERROR_EVENT = 'raiseError';

/**
 * Internal API event
 * @property {Error} error - The error raised from the editor
 */
export const BACKEND_ERROR_EVENT = 'raiseBackendError';

/**
 * Internal API event
 * @property coords - The coordinates of the selection
 * @property range - The range of the selection
 */
export const ANNOTATABLE_SELECTION_EVENT = 'annotatableSelection';

/**
 * Internal API event
 * No data
 */
export const ANNOTATABLE_SELECTION_CLEAR_EVENT = 'annotatableSelectionClear';


/**
 * External API event
 * @property {boolean} readOnlyValue - Toggle read only mode on/off
 */
export const READ_ONLY_EVENT = 'toggleReadOnly';

/**
 * External API event
 * @property {number} index - The index of the selected version from the version list
 */
export const SET_STATE_FROM_VERSION_EVENT = 'buildStateFromVersion';

/**
 * External API event
 * @property {boolean} diffModeValue - Toggle diff mode on/off
 */
export const DIFF_MODE_EVENT = 'toggleDiffMode';

/**
 * External API event
 * @property {boolean} historyModeValue - Toggle diff mode on/off
 */
export const HISTORY_MODE_EVENT = 'toggleHistoryMode';

/**
 * External API event
 * No data
 */
export const CREATE_TMP_ANNOTATION_EVENT = 'createTmpAnnotation';

/**
 * External API event
 * @property {string} uuid - UUID to identify the unique mark/annotation
 * @property coords - The coordinates of the selected annotation
 */
export const CREATE_ANNOTATION_EVENT = 'createAnnotation';

/**
 * External API event
 * No data
 */
export const CANCEL_CREATE_ANNOTATION_EVENT = 'cancelCreateAnnotation';

/**
 * Internal API event
 * @property {string} uuid - UUID to identify the unique mark/annotation
 */
export const ACTIVATE_ANNOTATION_EVENT = 'activateAnnotation';

/**
 * Internal API event
 * @property {string} uuid - UUID to identify the unique mark/annotation
 */
export const REMOVE_ANNOTATION_EVENT = 'removeAnnotation';

const ANNOTATION_URL = '/api/v2/annotations';

export function toggleReadOnlyMode(editorView, readOnlyValue) {
  const uiState = uiStatePluginKey.getState(editorView.state);
  editorView.dispatch(
    editorView.state.tr.setMeta(uiStatePluginKey, { ...uiState, readOnly: readOnlyValue })
  );
}

export function toggleDiffMode(editorView, diffModeValue) {
  const uiState = uiStatePluginKey.getState(editorView.state);
  editorView.dispatch(
    editorView.state.tr.setMeta(uiStatePluginKey, { ...uiState, showDiff: diffModeValue })
  );
}

export function toggleHistoryMode(editorView, historyModeValue) {
  const uiState = uiStatePluginKey.getState(editorView.state);
  editorView.dispatch(
    editorView.state.tr.setMeta(uiStatePluginKey, { ...uiState, historyMode: historyModeValue })
  );
}

export function bindEditorViewApi(consumerApi, editorView) {
  consumerApi.on(READ_ONLY_EVENT, (readOnlyValue) => {
    toggleReadOnlyMode(editorView, readOnlyValue);
  });

  consumerApi.on(SET_STATE_FROM_VERSION_EVENT, ({ newDoc, changeSet }) => {
    let state = editorView.state;
    const uiState = uiStatePluginKey.getState(state);
    const content = newDoc.content;
    const newState = {
      doc: {
        type: 'doc',
        content
      },
      selection: { type: 'text', anchor: 1, head: 1 }
    };
    state = EditorState.fromJSON({ schema: state.schema, plugins: state.plugins }, newState);
    editorView.updateState(state);
    const tr = state.tr;
    let trWithMeta = tr.setMeta(uiStatePluginKey, uiState);

    if (changeSet) {
      trWithMeta.setMeta(trackedPluginKey, {
        type: 'compareVersions',
        changeSet
      });
    }
    editorView.dispatch(trWithMeta);
  });

  consumerApi.on(DIFF_MODE_EVENT, (diffModeValue) => {
    toggleDiffMode(editorView, diffModeValue);
  });

  consumerApi.on(HISTORY_MODE_EVENT, (historyModeValue) => {
    toggleHistoryMode(editorView, historyModeValue);
  });

  consumerApi.on(CREATE_TMP_ANNOTATION_EVENT, () => {
    editorView.dispatch(
      editorView.state.tr.setMeta(annotationPluginKey, { type: 'createTmpAnnotation' })
    );
  });

  consumerApi.on(CREATE_ANNOTATION_EVENT, async (data) => {
    const { state: { selection } } = editorView;

    const postData = {
      ...data,
      editor_selection: selection,
      editor_doc_json: editorView.state.doc.toJSON()
    };

    try {
      const apiRes = await fetch(ANNOTATION_URL, {
        method: "POST",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(postData)
      });

      const respBody = await apiRes.json();
      if (apiRes.ok) {
        editorView.dispatch(
          editorView.state.tr
          .setMeta(annotationPluginKey, { type: 'createAnnotation', uuid: data.mark_id })
          .addMark(
            editorView.state.selection.from,
            editorView.state.selection.to,
            editorView.state.schema.marks.annotation.create({ uuid: data.mark_id })
          )
        );
        consumerApi.emit(ACTIVATE_ANNOTATION_EVENT, {
          annotationUUID: data.mark_id,
          coords: coordsAtPos(editorView, editorView.state.selection.from)
        });
      } else if (apiRes.status === 409) { // 409: client-server conflict
        data.setShowCommentForm(false);
        const msg = respBody.message || 'There was an error. Please refresh the page and try again.';
        console.error(msg);
      } else {
        data.setShowCommentForm(false);
        console.error('Error making api request, results are: ', apiRes);
      }
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Annotation creation error:`, {
        markId: data.mark_id,
        error: error.message
      });
    }
  });

  consumerApi.on(REMOVE_ANNOTATION_EVENT, async (uuid) => {
    const postData = {
      resolved: 1
    };

    const apiRes = await fetch(`${ANNOTATION_URL}/${uuid}`, {
      method: "PUT",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(postData)
    });
    const respBody = await apiRes.json();
    if (apiRes.ok) {
      const $to = editorView.state.doc.content.size;

      editorView.state.doc.nodesBetween(1, $to, (node, pos) => {
        if (node.marks.length > 0 && node.marks[0].attrs.uuid === uuid) {
          editorView.dispatch(
            editorView.state.tr
            .setMeta(annotationPluginKey, { type: 'removeAnnotation', uuid })
            .removeMark(pos, pos + node.nodeSize, editorView.state.schema.marks.annotation)
          );
        }
      });
    }
    else {
      console.error('Error removing annotation, please refresh the page and try again.');
    }
  });

  consumerApi.on(CANCEL_CREATE_ANNOTATION_EVENT, () => {
    editorView.dispatch(
      editorView.state.tr.setMeta(annotationPluginKey, { type: 'cancelCreateAnnotation' })
    );
  });
}



// WEBPACK FOOTER //
// ./app/javascript/client/editor-ui/api.js